// Top-level error boundary that replaces the Datadog ErrorBoundary wrapper.
// Its job beyond reporting: auto-recover from deploy-skew chunk failures. A
// chunk that fails to fetch or link rejects its dynamic import, which React
// surfaces here as a render error; we reload once to pick up the current build.
// Genuine app errors (and chunk errors that persist past the one reload) fall
// through to a friendly fallback and are reported to RUM. This boundary renders
// outside the Mantine provider, so the fallback uses plain markup only.
import React from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  clearChunkReloadGuard,
  isChunkLoadError,
  isReloadInitiated,
  onChunkReloadCancelled,
  reloadOnceForChunkError,
  wasChunkReloadCancelled,
} from 'utils/chunkReload';

import { ChunkErrorFallback } from './ChunkErrorFallback';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

// 'reloading' renders nothing while a chunk-error reload is pending, so the
// fallback UI never flashes before the page reloads. 'failed' shows the
// fallback (genuine error, or a chunk error whose reload was skipped).
type BoundaryStatus = 'ok' | 'reloading' | 'failed';

interface AppErrorBoundaryState {
  status: BoundaryStatus;
}

// How long the app must stay mounted without a chunk error before we re-arm
// the one-time reload guard. A reload loop never reaches this window (the page
// reloads first), so the guard survives across the loop and stops it; only a
// genuinely healthy session clears it, re-arming recovery for a future deploy.
const GUARD_REARM_DELAY_MS = 10_000;

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  override state: AppErrorBoundaryState = { status: 'ok' };
  private rearmTimer: ReturnType<typeof setTimeout> | undefined;
  private unsubscribeCancellation: (() => void) | undefined;

  private stopWatchingCancellation = (): void => {
    this.unsubscribeCancellation?.();
    this.unsubscribeCancellation = undefined;
  };

  private watchReloadCancellation = (
    error: Error,
    errorInfo: React.ErrorInfo
  ): void => {
    if (this.unsubscribeCancellation) {
      return;
    }
    this.unsubscribeCancellation = onChunkReloadCancelled(() => {
      this.setState({ status: 'failed' });
      datadogRum.addError(error, {
        feature: 'app_error_boundary',
        componentStack: errorInfo.componentStack,
        chunkReloadCancelled: true,
      });
    });
  };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    // Render nothing (not the fallback) for chunk errors while
    // componentDidCatch decides whether to reload — this avoids a flash of the
    // error UI before recovery. Returning a non-children state also stops React
    // from re-rendering (and re-throwing) the failed subtree.
    if (isChunkLoadError(error)) {
      return { status: 'reloading' };
    }
    return { status: 'failed' };
  }

  override componentDidMount(): void {
    // Re-arm only after a stable, error-free window — not on mount, since the
    // boundary mounts before lazy routes resolve (a Suspense fallback counts
    // as a mount). Clearing too early would let a persistent lazy-chunk error
    // reload forever.
    this.rearmTimer = setTimeout(clearChunkReloadGuard, GUARD_REARM_DELAY_MS);
  }

  override componentWillUnmount(): void {
    clearTimeout(this.rearmTimer);
    this.stopWatchingCancellation();
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // An error means this session is not healthy; cancel the re-arm so the
    // guard is never cleared out from under a recovery attempt.
    clearTimeout(this.rearmTimer);

    if (isChunkLoadError(error)) {
      // Triggers a one-time reload; only returns false once the guard is set
      // (or can't be persisted), i.e. the error survived a reload or recovery
      // isn't safe. While the reload is pending we stay in 'reloading' (null).
      if (reloadOnceForChunkError(error, 'react_render')) {
        this.watchReloadCancellation(error, errorInfo);
        return;
      }
      // A reload may already be scheduled this tick by another path (e.g.
      // vite:preloadError catching the same failed import); stay null rather
      // than flash the fallback before navigation commits.
      if (isReloadInitiated()) {
        this.watchReloadCancellation(error, errorInfo);
        return;
      }
      this.setState({ status: 'failed' });
      datadogRum.addError(error, {
        feature: 'app_error_boundary',
        componentStack: errorInfo.componentStack,
        chunkReloadCancelled: wasChunkReloadCancelled(),
      });
      return;
    }

    datadogRum.addError(error, {
      feature: 'app_error_boundary',
      componentStack: errorInfo.componentStack,
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): React.ReactNode {
    if (this.state.status === 'ok') {
      return this.props.children;
    }
    if (this.state.status === 'reloading') {
      return null;
    }

    return <ChunkErrorFallback onReload={this.handleReload} />;
  }
}
